import SEO from '@/components/SEO';
import { site } from '@/data/content';
import { Ticket, ArrowUpRight, Check, MessageSquare, Tag, Users, Clock, Workflow, BarChart3, FileText, Building2, ShoppingBag, HeartPulse, GraduationCap } from 'lucide-react';

const features = [
  { icon: MessageSquare, title: 'Unified ticketing', text: 'Create tickets from any channel — email, WhatsApp, live chat, Instagram, Telegram — in one workspace.' },
  { icon: Tag, title: 'Smart tagging', text: 'Auto-tag tickets by topic, priority, or customer segment for instant filtering and routing.' },
  { icon: Users, title: 'Team assignment', text: 'Assign tickets to agents, groups, or round-robin across your team with one click.' },
  { icon: Workflow, title: 'Custom workflows', text: 'Build escalation rules, SLA policies, and auto-responses tailored to your process.' },
  { icon: FileText, title: 'Rich context', text: 'Attach files, link orders, and view customer history without leaving the ticket.' },
  { icon: BarChart3, title: 'Ticket analytics', text: 'Track resolution time, backlog, CSAT, and agent performance in real time.' },
];

const setupSteps = [
  { step: '1', title: 'Connect your channels', text: 'Add email integrations, WhatsApp, Instagram, Telegram, or embed the live chat widget.' },
  { step: '2', title: 'Configure workflows', text: 'Set up auto-response rules, escalation policies, and SLA timers for your team.' },
  { step: '3', title: 'Train your team', text: 'Onboard agents in minutes with canned responses and knowledge base integration.' },
  { step: '4', title: 'Go live', text: 'Start receiving tickets across all channels in a single unified inbox.' },
];

const howItWorks = [
  { icon: MessageSquare, title: 'Message arrives', text: 'Customer sends an email, WhatsApp message, or live chat inquiry.' },
  { icon: Tag, title: 'Auto-created ticket', text: 'System automatically creates a ticket with customer context and channel source.' },
  { icon: Users, title: 'Smart assignment', text: 'Ticket routes to the right agent or team based on rules or round-robin.' },
  { icon: Check, title: 'Resolve and track', text: 'Agent responds, updates status, and analytics track the resolution journey.' },
];

const useCases = [
  { title: 'Overflow deflection', text: 'Let Zyra AI handle common FAQs while agents focus on complex tickets.' },
  { title: 'SLA compliance', text: 'Set response and resolution targets with automatic escalation when SLAs are breached.' },
  { title: 'Team collaboration', text: 'Share internal notes, assign ownership, and hand off tickets seamlessly.' },
];

const industryUseCases = [
  { icon: ShoppingBag, title: 'E-commerce', text: 'Handle order questions, refunds, and shipping across WhatsApp, Instagram, and live chat.' },
  { icon: Building2, title: 'SaaS', text: 'Turn trials into customers with proactive ticket management and self-serve docs.' },
  { icon: HeartPulse, title: 'Healthcare', text: 'Schedule appointments, answer patient questions, and keep care teams aligned securely.' },
  { icon: GraduationCap, title: 'Education', text: 'Admissions, student support, and parent queries in a single shared inbox.' },
];

const timeSavings = [
  { metric: '40%', label: 'Faster first response', text: 'With smart routing and auto-assignment' },
  { metric: '30%', label: 'Fewer repeat contacts', text: 'Through knowledge base integration' },
  { metric: '2x', label: 'Faster resolution', text: 'With unified customer context' },
  { metric: '50%', label: 'Time saved on routing', text: 'With automated workflows' },
];

const scalability = [
  { title: '1 agent', plan: 'Free', text: 'Core ticketing for one agent to get started.' },
  { title: '5-100 agents', plan: 'Pro', text: 'Unlimited tickets, all channels, AI replies, and analytics.' },
  { title: '100+ agents', plan: 'Enterprise', text: 'Custom SLAs, SSO, private cloud, and 24/7 support.' },
];

const comparison = [
  { feature: 'Unified inbox (chat, email, social)', zurihub: true, zendesk: 'partial', intercom: false },
  { feature: 'Built-in AI reply suggestions', zurihub: true, zendesk: 'partial', intercom: true },
  { feature: 'Free plan for 1 agent', zurihub: true, zendesk: false, intercom: false },
  { feature: 'Starting price per agent', zurihub: '$25', zendesk: '$99', intercom: '$99' },
  { feature: 'Native WhatsApp & Telegram', zurihub: true, zendesk: 'add-on', intercom: 'partial' },
  { feature: 'Ticket deflection from knowledge base', zurihub: true, zendesk: 'partial', intercom: false },
];

export default function Tickets() {
  return (
    <>
      <SEO
        title="Ticket Management"
        description="Assign, tag, prioritize, and automate tickets so nothing falls through the cracks. Unified ticketing across all channels with ZuriHub."
        path="/tickets"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <Ticket className="h-4 w-4" /> Ticket Management
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Never let a <span className="gradient-text">customer issue</span> fall through
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            Assign, tag, prioritize, and automate tickets across email, WhatsApp, live chat, and social — all in one unified workspace.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
          >
            Try ticket management <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Ticket features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to manage customer issues at scale.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="setup-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="setup-heading" className="text-center section-title">How to setup</h2>
          <p className="section-subtitle mx-auto mt-4 text-center">Get your ticket system running in minutes, not weeks.</p>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {setupSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-xl font-bold text-white shadow-lg">
                  {step.step}
                </div>
                <h3 className="mt-4 font-bold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="section-title">How it works</h2>
              <p className="mt-4 text-lg text-slate-600">
                When a customer reaches out from any channel, ZuriHub automatically creates a ticket with full context. Smart routing assigns it to the right agent, and workflows ensure SLAs are met.
              </p>
              <div className="mt-8 space-y-4">
                {howItWorks.map((item, index) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-bold text-brand-700">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                        <item.icon className="h-4 w-4 text-brand-600" />
                        {item.title}
                      </h4>
                      <p className="mt-1 text-sm text-slate-600">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-xl bg-slate-800 p-4">
                  <MessageSquare className="h-5 w-5 text-brand-400" />
                  <div>
                    <p className="font-semibold">New message received</p>
                    <p className="text-sm text-slate-400">WhatsApp from +1 555 0123</p>
                  </div>
                  <span className="ml-auto rounded-full bg-green-500/20 px-2 py-1 text-xs text-green-400">Auto-created</span>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-slate-800 p-4">
                  <Tag className="h-5 w-5 text-brand-400" />
                  <div>
                    <p className="font-semibold">Ticket #TKT-123456</p>
                    <p className="text-sm text-slate-400">Priority: High • Category: Billing</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-slate-800 p-4">
                  <Users className="h-5 w-5 text-brand-400" />
                  <div>
                    <p className="font-semibold">Assigned to: Sarah K.</p>
                    <p className="text-sm text-slate-400">SLA: 2 hours • Status: In Progress</p>
                  </div>
                </div>
                <div className="rounded-xl bg-brand-600 p-4 text-center text-sm font-semibold">
                  <Check className="mx-auto mb-2 h-5 w-5" />
                  Ticket resolved in 1h 45m
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="industry-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="industry-heading" className="text-center section-title">Industry use cases</h2>
          <p className="section-subtitle mx-auto mt-4 text-center">Teams across industries use ZuriHub tickets to deliver better support.</p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {industryUseCases.map((industry) => (
              <div key={industry.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-600">
                  <industry.icon className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-slate-900">{industry.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{industry.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="time-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="time-heading" className="text-center section-title">How much time it saves</h2>
          <p className="section-subtitle mx-auto mt-4 text-center">Real metrics from teams using ZuriHub ticket management.</p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {timeSavings.map((saving) => (
              <div key={saving.label} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm text-center">
                <div className="text-3xl font-extrabold text-brand-600">{saving.metric}</div>
                <div className="mt-2 font-semibold text-slate-900">{saving.label}</div>
                <p className="mt-2 text-sm text-slate-600">{saving.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="scalability-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="scalability-heading" className="text-center section-title">Scalability</h2>
          <p className="section-subtitle mx-auto mt-4 text-center">From your first agent to your thousandth.</p>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {scalability.map((tier) => (
              <div key={tier.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <div className="text-2xl font-extrabold text-slate-900">{tier.title}</div>
                <div className="mt-2 text-sm font-semibold text-brand-600">{tier.plan}</div>
                <p className="mt-4 text-sm text-slate-600">{tier.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 id="compare-heading" className="mb-10 text-center section-title">How we compare</h2>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left font-bold text-slate-700">Feature</th>
                  <th className="px-6 py-4 text-center font-bold text-brand-700">ZuriHub</th>
                  <th className="px-6 py-4 text-center font-bold text-slate-700">Zendesk</th>
                  <th className="px-6 py-4 text-center font-bold text-slate-700">Intercom</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.feature} className="border-t border-slate-100">
                    <td className="px-6 py-4 font-medium text-slate-900">{row.feature}</td>
                    <td className="px-6 py-4 text-center">
                      {row.zurihub === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="font-semibold text-brand-700">{row.zurihub}</span>}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {row.zendesk === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.zendesk}</span>}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {row.intercom === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.intercom}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="sla-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="sla-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">Reliability & team</h2>
              <p className="mt-4 text-slate-300">
                ZuriHub ticket management runs on the same global edge as Zuri Cloud. A standard backend engineering team monitors uptime, performance, and data integrity.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-slate-800/50 p-5">
                  <Users className="h-6 w-6 text-brand-400" />
                  <p className="mt-2 font-bold">Engineering team</p>
                  <p className="text-sm text-slate-400">Backend and platform engineers on call</p>
                </div>
                <div className="rounded-2xl bg-slate-800/50 p-5">
                  <Clock className="h-6 w-6 text-brand-400" />
                  <p className="mt-2 font-bold">Uptime target</p>
                  <p className="text-sm text-slate-400">99.99% on Enterprise plans</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">SLA highlights</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> 99.99% uptime on Enterprise plans</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Median API response under 200ms</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Encrypted ticket data at rest and in transit</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Daily backups with point-in-time recovery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start managing tickets today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Try ticket management free with one agent. Scale as you grow with transparent pricing.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
